import { getApiUrl } from '@/utils/apiConfig';
import { create } from 'zustand';
import type { Lang } from "@/i18n/translations";

export interface QuizOption {
  label: Record<Lang, string>;
  scores: Record<string, number>;
}

export interface QuizQuestion {
  id: string;
  question: Record<Lang, string>;
  options: QuizOption[];
  status: 'en_attente' | 'valide' | 'rejete';
  submittedBy: string; // 'admin' ou partyAcronym
  createdAt: string;
}

export interface QuizAnalytics {
  totalPlays: number;
}

interface QuizStore {
  questions: QuizQuestion[];
  analytics: QuizAnalytics;
  consolidatedParties: string[];
  isLoading: boolean;
  itemsPerSession: number;
  updateItemsPerSession: (num: number) => Promise<void>;
  fetchQuestions: () => Promise<void>;
  addQuestion: (q: Omit<QuizQuestion, 'id' | 'createdAt'>) => Promise<void>;
  updateQuestion: (id: string, q: Partial<QuizQuestion>) => Promise<void>;
  updateQuestionStatus: (id: string, status: QuizQuestion['status']) => Promise<void>;
  deleteQuestion: (id: string) => Promise<void>;
  recordPlay: () => Promise<void>;
  setPartyOptionChoice: (questionId: string, optionIndex: number, partyAcronym: string) => Promise<void>;
  publishPartyChoices: (partyAcronym: string) => Promise<void>;
  resetAnalytics: () => Promise<void>;
}

const initialQuestions: QuizQuestion[] = [
  {
    id: "q1",
    question: {
      fr: "Quelle est votre priorité pour le développement du Maroc ?",
      ar: "ما هي أولويتك لتنمية المغرب؟",
      zgh: "ⵎⴰⵜⵜⴰ ⵜⴰⵎⵣⵡⴰⵔⵓⵜ ⵏⵏⴽ ⵉ ⵜⴳⵎⵉ ⵏ ⵍⵎⵖⵔⵉⴱ?",
    },
    options: [
      { label: { fr: "Croissance économique et investissement", ar: "النمو الاقتصادي والاستثمار", zgh: "ⴰⵏⴳⵔⴰⵡ ⴰⴷⵎⵙⴰⵏ ⴷ ⵓⵙⵙⴰⵔⵓ" }, scores: { RNI: 3, UC: 2, PAM: 1 } },
      { label: { fr: "Justice sociale et droits des travailleurs", ar: "العدالة الاجتماعية وحقوق العمال", zgh: "ⵜⴰⵣⵔⴼⵜ ⵜⴰⵏⴰⵎⵓⵏⵜ ⴷ ⵉⵣⵔⴼⴰⵏ ⵏ ⵉⵎⵙⵡⵓⵔⵉⵏ" }, scores: { USFP: 3, PPS: 2, PSU: 1 } },
      { label: { fr: "Développement rural et agriculture", ar: "التنمية القروية والفلاحة", zgh: "ⵜⴰⴳⵎⵉ ⵜⴰⴼⴰⵔⴰⵏⵜ ⴷ ⵜⴰⴽⵔⵣⴰ" }, scores: { MP: 3, PI: 1 } },
      { label: { fr: "Environnement et développement durable", ar: "البيئة والتنمية المستدامة", zgh: "ⴰⵡⵏⴰⴹ ⴷ ⵜⴰⴳⵎⵉ ⵜⴰⵙⵓⵍⴰⵏⵜ" }, scores: { PPS: 2, PEDD: 3, PGVM: 2 } },
    ],
    status: 'valide',
    submittedBy: 'admin',
    createdAt: new Date().toISOString()
  },
  {
    id: "q2",
    question: {
      fr: "Quel rôle devrait jouer l'État dans l'économie ?",
      ar: "ما الدور الذي يجب أن تلعبه الدولة في الاقتصاد؟",
      zgh: "ⵎⴰⵜⵜⴰ ⵜⴰⵡⵓⵔⵉ ⴰⴷ ⵜⵓⵔⴰⵔ ⵜⴰⵎⴰⵡⴰⵙⵜ ⴳ ⵜⴰⴷⵎⵙⴰ?",
    },
    options: [
      { label: { fr: "Libéraliser et encourager le secteur privé", ar: "تحرير وتشجيع القطاع الخاص", zgh: "ⴰⵙⵍⴻⵍⵍⵉ ⴷ ⵓⵙⵎⵓⵙⵙⵓ ⵏ ⵓⴳⵣⵣⵓⵎ ⵓⵙⵍⵉⴳ" }, scores: { RNI: 3, PML: 2, UC: 2 } },
      { label: { fr: "Réguler fortement pour protéger les citoyens", ar: "تنظيم قوي لحماية المواطنين", zgh: "ⴰⵙⵏⴰⵎ ⴰⵎⵇⵔⴰⵏ ⵉ ⵓⵎⵣⴰⵖ ⵏ ⵉⵎⵖⵔⴰⴱⵉⵢⵏ" }, scores: { USFP: 3, PPS: 2, PT: 2 } },
      { label: { fr: "Équilibre entre intervention étatique et libre marché", ar: "التوازن بين تدخل الدولة والسوق الحرة", zgh: "ⴰⵎⵢⴰⵡⴰⴹ ⴳⵔ ⵜⵉⴷⵓⴽⵍⴰ ⵏ ⵜⵎⴰⵡⴰⵙⵜ ⴷ ⵓⵙⵓⵇ ⵉⵍⴻⵍⵍⵉ" }, scores: { PAM: 2, PI: 2, PCS: 2 } },
      { label: { fr: "Moralisation et éthique dans la gestion publique", ar: "الأخلاق والنزاهة في التدبير العمومي", zgh: "ⵜⴰⵥⵍⴰⵢⵜ ⴷ ⵜⴰⵎⵢⵓⵔⵜ ⴳ ⵓⵙⵡⵓⴷⴷⵓ ⴰⴳⴷⵓⴷⴰⵏ" }, scores: { PJD: 3, PRV: 2 } },
    ],
    status: 'valide',
    submittedBy: 'admin',
    createdAt: new Date().toISOString()
  },
  {
    id: "q3",
    question: {
      fr: "Quelle approche privilégiez-vous pour l'identité nationale ?",
      ar: "ما المقاربة التي تفضلها للهوية الوطنية؟",
      zgh: "ⵎⴰⵜⵜⴰ ⵜⴰⵎⵢⴰⴽⴰⵣⵜ ⴰⴷ ⵜⵙⵜⵉⵜ ⵉ ⵜⵎⴰⴳⵉⵜ ⵜⴰⵏⴰⵎⵓⵔⵜ?",
    },
    options: [
      { label: { fr: "Modernité et ouverture sur le monde", ar: "الحداثة والانفتاح على العالم", zgh: "ⵜⴰⵜⵔⴰⵔⵜ ⴷ ⵓⵏⵏⵓⵕⵥⵎ ⵅⴼ ⵓⵎⴰⴹⴰⵍ" }, scores: { PAM: 3, RNI: 2, PML: 1 } },
      { label: { fr: "Préservation des valeurs traditionnelles", ar: "الحفاظ على القيم التقليدية", zgh: "ⴰⵃⵟⵟⵓ ⵏ ⵡⴰⵜⵉⴳⵏ ⵉⵣⴰⵢⴽⵓⵜⵏ" }, scores: { PJD: 3, PI: 2, PRV: 1 } },
      { label: { fr: "Fierté nationale et souveraineté", ar: "الفخر الوطني والسيادة", zgh: "ⴰⵎⵇⵔⴰⵏ ⴰⵏⴰⵎⵓⵔ ⴷ ⵜⵉⵍⴻⵍⵍⵉ" }, scores: { PI: 3, CNI: 2, "Al Ahd": 1 } },
      { label: { fr: "Diversité culturelle et amazighité", ar: "التنوع الثقافي والأمازيغية", zgh: "ⴰⵎⵢⴰⵏⴰⵡ ⴰⴷⵍⵙⴰⵏ ⴷ ⵜⵎⴰⵣⵉⵖⵜ" }, scores: { PAM: 2, USFP: 1, PSU: 2 } },
    ],
    status: 'valide',
    submittedBy: 'admin',
    createdAt: new Date().toISOString()
  },
  {
    id: "q4",
    question: {
      fr: "Quelle est votre vision de la gouvernance ?",
      ar: "ما رؤيتك للحكامة؟",
      zgh: "ⵎⴰⵜⵜⴰ ⵜⵉⵖⵔⵉ ⵏⵏⴽ ⵏ ⵓⵙⵡⵓⴷⴷⵓ?",
    },
    options: [
      { label: { fr: "Décentralisation et pouvoir local", ar: "اللامركزية والسلطة المحلية", zgh: "ⴰⵙⵎⵓⵙⵙⵓ ⴰⴷⵖⴰⵔⴰⵏ" }, scores: { PAM: 2, MP: 3, PRD: 2 } },
      { label: { fr: "Renforcement des institutions centrales", ar: "تعزيز المؤسسات المركزية", zgh: "ⴰⵙⵖⵓⴷⵓ ⵏ ⵜⵎⵔⵙⴰⵍ ⵜⵉⵏⴰⵎⵎⴰⵙⵉⵏ" }, scores: { PI: 2, UC: 2, PND: 2 } },
      { label: { fr: "Démocratie participative et société civile", ar: "الديمقراطية التشاركية والمجتمع المدني", zgh: "ⵜⴰⴷⵉⵎⵓⵇⵔⴰⵜⵉⵜ ⵜⴰⵎⵛⴰⵔⴽⴰⵜ" }, scores: { USFP: 2, PPS: 2, CNI: 2, PSU: 2 } },
      { label: { fr: "Transparence et lutte contre la corruption", ar: "الشفافية ومحاربة الفساد", zgh: "ⵜⴰⵙⵓⵍⵜ ⴷ ⵓⵎⵏⵖⵉ ⵏ ⵜⴰⵎⵅⵅⵉⵕⵜ" }, scores: { PJD: 2, PAM: 1, "Al Ahd": 2 } },
    ],
    status: 'valide',
    submittedBy: 'admin',
    createdAt: new Date().toISOString()
  },
  {
    id: "q5",
    question: {
      fr: "Quel enjeu social vous tient le plus à cœur ?",
      ar: "ما القضية الاجتماعية الأقرب إلى قلبك؟",
      zgh: "ⵎⴰⵜⵜⴰ ⵜⴰⵎⵙⴰⵍⵜ ⵜⴰⵏⴰⵎⵓⵏⵜ ⴰⴷ ⵉⵣⴷⵉⵏ ⵓⴳⴳⴰⵔ?",
    },
    options: [
      { label: { fr: "Éducation et formation professionnelle", ar: "التعليم والتكوين المهني", zgh: "ⴰⵙⵍⵎⴷ ⴷ ⵓⵙⵎⵓⵜⵜⴳ ⴰⵣⵣⵓⵍⴰⵏ" }, scores: { PI: 2, USFP: 2, RNI: 1 } },
      { label: { fr: "Santé et protection sociale", ar: "الصحة والحماية الاجتماعية", zgh: "ⵜⴰⴷⵓⵙⵉ ⴷ ⵓⵎⵣⴰⵖ ⴰⵏⴰⵎⵓⵏ" }, scores: { PPS: 2, USFP: 2, PT: 2 } },
      { label: { fr: "Emploi des jeunes et entrepreneuriat", ar: "تشغيل الشباب وريادة الأعمال", zgh: "ⴰⵙⵡⵓⵔⵉ ⵏ ⵉⵄⵔⵔⵉⵎⵏ ⴷ ⵜⴰⵏⴱⴰⴹⵜ ⵏ ⵜⵎⵙⵙⵏⵜⵉⵜ" }, scores: { RNI: 2, PAM: 2, PML: 1 } },
      { label: { fr: "Droits des femmes et égalité", ar: "حقوق المرأة والمساواة", zgh: "ⵉⵣⵔⴼⴰⵏ ⵏ ⵜⵎⵖⴰⵔⵜ ⴷ ⵜⴰⵏⵏⴰⵍⵜ" }, scores: { USFP: 2, PAM: 1, PSU: 2, PLJS: 2 } },
    ],
    status: 'valide',
    submittedBy: 'admin',
    createdAt: new Date().toISOString()
  },
  {
    id: "q6",
    question: {
      fr: "Comment voyez-vous la politique étrangère du Maroc ?",
      ar: "كيف ترى السياسة الخارجية للمغرب؟",
      zgh: "ⵎⴰⵎⴽ ⴰⴷ ⵜⵥⵕⵜ ⵜⴰⵙⵔⵜⵉⵜ ⵜⴰⴱⵕⵕⴰⵏⵜ ⵏ ⵍⵎⵖⵔⵉⴱ?",
    },
    options: [
      { label: { fr: "Partenariats économiques internationaux", ar: "شراكات اقتصادية دولية", zgh: "ⵜⵉⵎⵢⴰⵎⴰⵣⵉⵏ ⵜⵉⴷⵎⵙⴰⵏⵉⵏ ⵜⵉⴳⵔⴰⵖⵍⴰⵏⵉⵏ" }, scores: { RNI: 3, UC: 1 } },
      { label: { fr: "Coopération Sud-Sud et africanité", ar: "التعاون جنوب-جنوب والانتماء الأفريقي", zgh: "ⴰⵎⵢⴰⵡⴰⵙ ⴰⵏⵥⵓⵍ-ⴰⵏⵥⵓⵍ" }, scores: { PI: 2, PAM: 2 } },
      { label: { fr: "Solidarité avec les causes arabes et islamiques", ar: "التضامن مع القضايا العربية والإسلامية", zgh: "ⵜⴰⵡⵉⵣⵉ ⴷ ⵜⵎⵙⴰⵍⵉⵏ ⵜⵉⵄⵔⴰⴱⵉⵏ" }, scores: { PJD: 3, PI: 1 } },
      { label: { fr: "Neutralité et souveraineté nationale", ar: "الحياد والسيادة الوطنية", zgh: "ⵜⴰⵏⵏⴰⵍⵜ ⴷ ⵜⵉⵍⴻⵍⵍⵉ ⵜⴰⵏⴰⵎⵓⵔⵜ" }, scores: { MP: 2, CNI: 1, PDI: 2 } },
    ],
    status: 'valide',
    submittedBy: 'admin',
    createdAt: new Date().toISOString()
  },
  {
    id: "q7",
    question: {
      fr: "Quelle place pour la religion dans la politique ?",
      ar: "ما مكانة الدين في السياسة؟",
      zgh: "ⵎⴰⵜⵜⴰ ⴰⵏⵙⴰ ⵏ ⵓⴷⵉⵏ ⴳ ⵜⴰⵙⵔⵜⵉⵜ?",
    },
    options: [
      { label: { fr: "Les valeurs religieuses doivent guider la politique", ar: "القيم الدينية يجب أن توجه السياسة", zgh: "ⵡⴰⵜⵉⴳⵏ ⵏ ⵓⴷⵉⵏ ⴰⴷ ⵉⵙⵡⵓⴷⴷⵓⵏ ⵜⴰⵙⵔⵜⵉⵜ" }, scores: { PJD: 3, PRV: 2 } },
      { label: { fr: "Séparation claire entre religion et État", ar: "فصل واضح بين الدين والدولة", zgh: "ⴰⴱⴹⵓ ⵉⴱⴰⵏⵏ ⴳⵔ ⵓⴷⵉⵏ ⴷ ⵜⵎⴰⵡⴰⵙⵜ" }, scores: { USFP: 2, PSU: 3, PAM: 2 } },
      { label: { fr: "Respect des traditions sans imposer", ar: "احترام التقاليد دون فرض", zgh: "ⴰⵙⵎⵖⵔ ⵏ ⵉⵏⴰⵎⴽⵏ ⴱⵍⴰ ⴰⵙⵏⵢⵓⴹ" }, scores: { PI: 2, MP: 2, UC: 1 } },
      { label: { fr: "La religion est une affaire personnelle", ar: "الدين شأن شخصي", zgh: "ⴰⴷⵉⵏ ⴷ ⵜⴰⵎⵙⴰⵍⵜ ⵜⵓⴷⵎⴰⵡⴰⵏⵜ" }, scores: { PPS: 2, PML: 2, PGVM: 1 } },
    ],
    status: 'valide',
    submittedBy: 'admin',
    createdAt: new Date().toISOString()
  }
];

export const useQuizStore = create<QuizStore>((set, get) => ({
  questions: initialQuestions,
  itemsPerSession: 5,
  analytics: {
    totalPlays: 320,
  },
  consolidatedParties: [],
  isLoading: false,
  
  updateItemsPerSession: async (num) => {
    const validNum = Math.max(5, Math.min(20, num));
    set({ itemsPerSession: validNum });
    
    // Save to backend via settings API
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      
      await fetch(`${getApiUrl()}/settings.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ action: 'update_single', key: 'quiz_items_per_session', value: validNum.toString() })
      });
    } catch (e) {
      console.error("Failed to save itemsPerSession", e);
    }
  },

  fetchQuestions: async () => {
    set({ isLoading: true });
    try {
      const res = await fetch(`${getApiUrl()}/quiz.php?t=${Date.now()}`, {
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      if (!res.ok) throw new Error("API Error");
      const data = await res.json();
      
      if (data.questions && data.questions.length > 0) {
        set({ questions: data.questions });
      }
      if (data.analytics) {
        set({ analytics: data.analytics });
      }
      if (data.itemsPerSession !== undefined) {
        set({ itemsPerSession: data.itemsPerSession });
      }
      if (data.consolidatedParties) {
        set({ consolidatedParties: data.consolidatedParties });
      }
    } catch (e) {
      console.error("Failed to fetch quiz from DB, using fallback", e);
    } finally {
      set({ isLoading: false });
    }
  },

  addQuestion: async (q) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${getApiUrl()}/quiz.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'add', ...q })
      });
      await get().fetchQuestions();
    } catch (e) {
      console.error(e);
      // Fallback local update
      set((state) => ({ 
        questions: [...state.questions, { ...q, id: `q${Date.now()}`, createdAt: new Date().toISOString() }] 
      }));
    }
  },

  updateQuestion: async (id, data) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${getApiUrl()}/quiz.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'update', id, ...data })
      });
      await get().fetchQuestions();
    } catch (e) {
      console.error(e);
      set((state) => ({
        questions: state.questions.map(q => q.id === id ? { ...q, ...data } : q)
      }));
    }
  },

  updateQuestionStatus: async (id, status) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${getApiUrl()}/quiz.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'update_status', id, status })
      });
      await get().fetchQuestions();
    } catch (e) {
      console.error(e);
      set((state) => ({
        questions: state.questions.map(q => q.id === id ? { ...q, status } : q)
      }));
    }
  },

  deleteQuestion: async (id) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${getApiUrl()}/quiz.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'delete', id })
      });
      await get().fetchQuestions();
    } catch (e) {
      console.error(e);
      set((state) => ({
        questions: state.questions.filter(q => q.id !== id)
      }));
    }
  },

  recordPlay: async () => {
    set((state) => ({
      analytics: { totalPlays: state.analytics.totalPlays + 1 }
    }));
    try {
      await fetch(`${getApiUrl()}/quiz.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'record_play' })
      });
    } catch (e) {
      console.error(e);
    }
  },

  setPartyOptionChoice: async (questionId, optionIndex, partyAcronym) => {
    set((state) => {
      const newQuestions = state.questions.map(q => {
        if (q.id === questionId) {
          const newOptions = q.options.map((opt, idx) => {
            const newScores = { ...opt.scores };
            if (idx === optionIndex) {
              newScores[partyAcronym] = 1;
            } else {
              delete newScores[partyAcronym];
            }
            return { ...opt, scores: newScores };
          });
          return { ...q, options: newOptions };
        }
        return q;
      });
      return { questions: newQuestions };
    });
    // Optional: send to backend
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${getApiUrl()}/quiz.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'party_choice', questionId, optionIndex, partyAcronym })
      });
      } catch (e) {
      console.error(e);
    }
  },

  publishPartyChoices: async (partyAcronym) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${getApiUrl()}/quiz.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'consolidate_party', partyAcronym })
      });
      set((state) => ({ consolidatedParties: [...new Set([...state.consolidatedParties, partyAcronym])] }));
    } catch (e) {
      console.error(e);
    }
  },

  resetAnalytics: async () => {
    set({ analytics: { totalPlays: 0 } });
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${getApiUrl()}/quiz.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'reset_analytics' })
      });
    } catch (e) {
      console.error(e);
    }
  }
}));

// Auto-fetch on init if in browser
if (typeof window !== "undefined") {
  useQuizStore.getState().fetchQuestions();
}
